import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCallAPI } from 'hooks/useCallAPI';
import { SiteAnnouncement, SiteAnnouncementStatus, SiteAnnouncementType } from 'core/siteAnnouncement/SiteAnnouncement';
import _ from 'lodash';
import { ColumnDefinition, getCustomRenderColumn, getNameIdColumn, getStatusDesColumn, renderColumn } from 'components/TableColumn/TableColumn';
import i18n from 'i18n';
import styles from './siteAnnouncementList.module.scss';
import { useCoreContext } from 'contexts/coreContext';
import { renderSiteAnnouncementNameIdWithDetailColumn } from './components/SiteAnnouncementNameIdWithDetailColumn';
import { DefaultSiteAnnouncementManager, SiteAnnouncementManager } from 'core/siteAnnouncement/SiteAnnouncementManager';
import { BatchOperation } from 'components/Table/StickableBootstrapTable';
import { toast } from 'react-toastify';
import { renderSiteAnnouncementContent } from 'components/SiteAnnouncement/SiteAnnouncementContent';

export type SiteAnnouncementListModel = {
  loading: boolean;
  isAdmin: boolean;
  searchString: string;
  filteredAnnouncements: SiteAnnouncement[];
  selectedAnnouncements: number[];
  columns: ColumnDefinition[];
  batchOperations?: {
    operation: BatchOperation;
  }[];
  onHandleSearch: (searchString: string) => void;
  onHandleSelect: (id: number | string, select: boolean) => void;
  onHandleSelectPage: (select: boolean) => void;
  onClearSelect: () => void;
  onPageChange: (page: number) => void;
  markAnnouncementsAsRead: () => void;
  getAnnouncementRowClasses?: (announcement: SiteAnnouncement) => string;
  showDeleteConfirmModal: boolean;
  deleteAnnouncementIds: number[];
  onShowDeleteConfirmModal: (ids: number[]) => void;
  onHideDeleteConfirmModal: () => void;
  onConfirmDelete: () => void;
};

export enum SiteAnnouncementListColumn {
  ID = 'id',
  NAME = 'name',
  CONTENT = 'content',
  VENDOR_NUMBERS = 'vendorNumbers',
  PUBLISH_TIME = 'publishTime',
  STATUS = 'status',
  CREATOR = 'creator'
}

const defaultSiteAnnouncementManager: SiteAnnouncementManager = new DefaultSiteAnnouncementManager();
export const SIZE_PER_PAGE: number = 10;
export const useSiteAnnouncementListModel = (
  type: SiteAnnouncementType,
  autoOpenAnnouncementId?: number
): SiteAnnouncementListModel => {
  const { loading, callAPIs } = useCallAPI();
  const [searchString, setSearchString] = useState<string>('');
  const [siteAnnouncements, setSiteAnnouncements] = useState<SiteAnnouncement[]>([]);
  const [selectedAnnouncements, setSelectedAnnouncements] = useState<number[]>([]);
  const core = useCoreContext();
  const authenticationManager = core?.authenticationManager;
  const isAdmin = authenticationManager?.actor?.roleName === 'ADMIN';
  const [page, setPage] = useState<number>(1);
  const [publishTimeSort, setPublishTimeSort] = useState<'asc' | 'desc'>();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [deleteAnnouncementIds, setDeleteAnnouncementIds] = useState<number[]>([]);

  const sortByPublishTime = useCallback(
    (a: SiteAnnouncement, b: SiteAnnouncement, sortOrder: 'asc' | 'desc') => {
      const aTime = new Date(a.publishTime).getTime();
      const bTime = new Date(b.publishTime).getTime();
      if (aTime < bTime) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aTime > bTime) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    },
    []
  );

  /**
   * 只留本頁分類的通知。
   * 這一層一定要做在 setSiteAnnouncements 之前（不可放進 filteredAnnouncements）：
   * firstPagesAnnouncements 是從未過濾的 siteAnnouncements 切出來的，
   * 做錯層級會讓 markAnnouncementsAsRead 標到別的分類，而且完全不會報錯。
   * type 缺席的舊資料視為 SYSTEM（BE backfill 前的相容期）。
   */
  const filterByType = useCallback(
    (announcements: SiteAnnouncement[]) =>
      announcements.filter(
        (announcement: SiteAnnouncement) =>
          (announcement.type ?? SiteAnnouncementType.SYSTEM) === type
      ),
    [type]
  );

  useEffect(() => {
    if (!authenticationManager) {
      return;
    }
    callAPIs(
      [() => defaultSiteAnnouncementManager.getSiteAnnouncements()],
      (results: SiteAnnouncement[]) => {
        const announcementsOfType = filterByType(results);
        const firstPageAnnouncements = announcementsOfType.slice(0, SIZE_PER_PAGE);
        firstPageAnnouncements.sort((a: SiteAnnouncement, b: SiteAnnouncement) => {
          const isARead = authenticationManager.hasReadAnnouncement(a);
          const isBRead = authenticationManager.hasReadAnnouncement(b);
          if (!isARead && isBRead) {
            return -1;
          }
          if (!isBRead && isARead) {
            return 1;
          }
          return 0;
        });
        setSiteAnnouncements([...firstPageAnnouncements, ...announcementsOfType.slice(SIZE_PER_PAGE)]);
      }
    );
  }, [authenticationManager, sortByPublishTime, callAPIs, filterByType]);

  const getColumnDefinition = useCallback((column: SiteAnnouncementListColumn) => {
    return {
      dataField: column,
      text: i18n.t<string>(`siteAnnouncementList.headers.${column}`),
      classes: styles[column]
    };
  }, []);

  const adminColumns = useMemo(() => {
    return _.compact([
      getNameIdColumn(
        getColumnDefinition(SiteAnnouncementListColumn.NAME),
        (announcement: SiteAnnouncement) =>
          `/site-announcements/${announcement.id}/edit`,
        true
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.CONTENT),
        (record: SiteAnnouncement) =>
          renderSiteAnnouncementContent(record.content, true, 2)
      ),
      renderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.VENDOR_NUMBERS),
        (value?: string[]) =>
          value && value.length > 0
            ? value.join(', ')
            : i18n.t<string>('siteAnnouncementList.labels.allSuppliers')
      ),
      {
        ...getColumnDefinition(SiteAnnouncementListColumn.PUBLISH_TIME),
        sort: true,
        onSort: (_1, order: 'asc' | 'desc') => {
          setPublishTimeSort(order);
        }
      },
      getStatusDesColumn(
        getColumnDefinition(SiteAnnouncementListColumn.STATUS),
        (announcement: SiteAnnouncement) => {
          switch (announcement.status) {
            case SiteAnnouncementStatus.PUBLISHED:
              return {
                des: i18n.t<string>('siteAnnouncementList.labels.published'),
                color: 'theme1'
              };
            case SiteAnnouncementStatus.UNPUBLISHED:
              return {
                des: i18n.t<string>('siteAnnouncementList.labels.unpublished'),
                color: 'light'
              };
            case SiteAnnouncementStatus.INACTIVE:
              return {
                des: i18n.t<string>('siteAnnouncementList.labels.inactive'),
                color: 'danger'
              };
            default:
              return { des: '', color: '' };
          }
        }
      ),
      getColumnDefinition(SiteAnnouncementListColumn.CREATOR)
    ]);
  }, [getColumnDefinition]);

  const normalColumns = useMemo(() => {
    return _.compact([
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.NAME),
        renderSiteAnnouncementNameIdWithDetailColumn(autoOpenAnnouncementId)
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.CONTENT),
        (record: SiteAnnouncement) =>
          renderSiteAnnouncementContent(record.content, true, 2)
      ),
      {
        ...getColumnDefinition(SiteAnnouncementListColumn.PUBLISH_TIME),
        sort: true,
        onSort: (_1, order: 'asc' | 'desc') => {
          setPublishTimeSort(order);
        }
      }
    ]);
  }, [getColumnDefinition, autoOpenAnnouncementId]);

  const columns = useMemo(
    () => (isAdmin ? adminColumns : normalColumns),
    [isAdmin, adminColumns, normalColumns]
  );

  const filteredAnnouncements = useMemo(() => {
    return siteAnnouncements.filter((announcement) => {
      const nameMatch = _.includes(
        announcement.name.toLowerCase(),
        searchString.toLowerCase()
      );
      const contentMatch = _.includes(
        announcement.content.toLowerCase(),
        searchString.toLowerCase()
      );
      const vendorNumbersMatch = isAdmin
        ? _.some(announcement.vendorNumbers || [], (vendorNumber) =>
            _.includes(vendorNumber.toLowerCase(), searchString.toLowerCase())
          ) ||
          (_.isEmpty(announcement.vendorNumbers) &&
            _.includes(
              i18n
                .t<string>('siteAnnouncementList.labels.allSuppliers')
                .toLowerCase(),
              searchString.toLowerCase()
            ))
        : false;
      return nameMatch || contentMatch || vendorNumbersMatch;
    });
  }, [isAdmin, siteAnnouncements, searchString]);

  const onHandleSearch = useCallback((searchString: string) => {
    setSearchString(searchString);
  }, []);

  const onHandleSelect = useCallback((id: number | string, select: boolean) => {
    setSelectedAnnouncements(prev => select ? _.uniq([...prev, +id]) : prev.filter(p => p.toString() !== id.toString()));
  }, []);

  const onHandleSelectPage = useCallback((select: boolean) => {
    const targetId = siteAnnouncements.map(row => row.id);
    setSelectedAnnouncements(prev => select ?
      _.uniq([...prev, ...targetId]) :
      prev.filter(p => !targetId.includes(p))
    );
  }, [siteAnnouncements]);

  const onClearSelect = useCallback(() => {
    setSelectedAnnouncements([]);
  }, []);

  // 處理批次操作後的通用回調函數
  const handleBatchOperationSuccess = useCallback((operationType: 'activate' | 'deactivate' | 'delete') => {
    callAPIs(
      [() => defaultSiteAnnouncementManager.getSiteAnnouncements()],
      (results: SiteAnnouncement[]) => {
        setSiteAnnouncements(filterByType(results));
        setSelectedAnnouncements([]);

        // 顯示成功提示訊息
        switch (operationType) {
          case 'activate':
            toast.success(i18n.t<string>('siteAnnouncementList.messages.activateSuccess'));
            break;
          case 'deactivate':
            toast.success(i18n.t<string>('siteAnnouncementList.messages.deactivateSuccess'));
            break;
          case 'delete':
            toast.success(i18n.t<string>('siteAnnouncementList.messages.deleteSuccess'));
            break;
        }
      }
    );
  }, [callAPIs, filterByType]);

  // 啟用
  const createActivateOperation = useCallback(() => {
    return {
      operation: {
        text: i18n.t<string>('siteAnnouncementList.buttons.activate'),
        hint: '',
        enable: true,
        onClick: () => {
          callAPIs(
            [() => defaultSiteAnnouncementManager.activateAnnouncements(selectedAnnouncements)],
            () => handleBatchOperationSuccess('activate')
          );
        }
      }
    };
  }, [selectedAnnouncements, callAPIs, handleBatchOperationSuccess]);

  // 停用
  const createDeactivateOperation = useCallback(() => {
    return {
      operation: {
        text: i18n.t<string>('siteAnnouncementList.buttons.deactivate'),
        hint: '',
        enable: true,
        onClick: () => {
          callAPIs(
            [() => defaultSiteAnnouncementManager.deactivateAnnouncements(selectedAnnouncements)],
            () => handleBatchOperationSuccess('deactivate')
          );
        }
      }
    };
  }, [selectedAnnouncements, callAPIs, handleBatchOperationSuccess]);

  // 共用刪除邏輯
  const executeDelete = useCallback((ids: number[]) => {
    callAPIs(
      [() => defaultSiteAnnouncementManager.deleteAnnouncements(ids)],
      () => {
        // Refresh the list after deletion
        callAPIs(
          [() => defaultSiteAnnouncementManager.getSiteAnnouncements()],
          (results: SiteAnnouncement[]) => {
            setSiteAnnouncements(filterByType(results));
            setSelectedAnnouncements([]);
            toast.success(i18n.t<string>('siteAnnouncementList.messages.deleteSuccess'));
          }
        );
      },
      (error: any) => {
        console.error('Delete announcement error:', error);
        toast.error(i18n.t<string>('siteAnnouncementList.messages.deleteError'));
      }
    );
  }, [callAPIs, filterByType]);

  // 刪除
  const createDeleteOperation = useCallback((selectedAnnouncementsData: SiteAnnouncement[]) => {
    // 檢查選中項中是否所有項都可刪除（即狀態為 UNPUBLISHED）
    const canDeleteAll = selectedAnnouncementsData.every(announcement =>
      announcement.status === SiteAnnouncementStatus.UNPUBLISHED
    );

    return {
      operation: {
        text: i18n.t<string>('common.buttons.delete'),
        hint: canDeleteAll ? '' : i18n.t<string>('siteAnnouncementList.hints.cannotDelete'),
        enable: canDeleteAll,
        onClick: () => {
          setDeleteAnnouncementIds(selectedAnnouncements);
          setShowDeleteConfirmModal(true);
        }
      }
    };
  }, [selectedAnnouncements]);

  const batchOperations = useMemo(() => {
    if (selectedAnnouncements.length === 0) {
      return [];
    }

    const selectedAnnouncementsData = siteAnnouncements.filter(announcement =>
      selectedAnnouncements.includes(announcement.id)
    );

    return [
      createActivateOperation(),
      createDeactivateOperation(),
      createDeleteOperation(selectedAnnouncementsData)
    ];
  }, [selectedAnnouncements, siteAnnouncements, createActivateOperation, createDeactivateOperation, createDeleteOperation]);

  const firstPagesAnnouncements = useMemo(() => {
    return siteAnnouncements.slice(0, SIZE_PER_PAGE);
  }, [siteAnnouncements]);

  const sortedAnnouncements = useMemo(() => {
    if (!publishTimeSort) {
      return filteredAnnouncements;
    }
    return [...filteredAnnouncements].sort((a, b) => {
      return sortByPublishTime(a, b, publishTimeSort);
    });
  }, [filteredAnnouncements, publishTimeSort, sortByPublishTime]);

  const markAnnouncementsAsRead = useCallback(() => {
    if (!authenticationManager || isAdmin) {
      return;
    }

    const currentPageFirstAnnouncementIndex = (page - 1) * SIZE_PER_PAGE;
    const currentPageFinalAnnouncementIndex =
      currentPageFirstAnnouncementIndex + SIZE_PER_PAGE - 1;
    // 目前頁面的通知
    const currentPageAnnouncements = sortedAnnouncements.slice(
      currentPageFirstAnnouncementIndex,
      currentPageFinalAnnouncementIndex + 1
    );

    // 只更新最原始第一頁通知訊息的讀取狀態
    const needToMarkAsReadAnnouncements = currentPageAnnouncements.filter(
      (announcement: SiteAnnouncement) => {
        return (
          firstPagesAnnouncements.find(
            (firstPageAnnouncement: SiteAnnouncement) => {
              return firstPageAnnouncement.id === announcement.id;
            }
          ) !== undefined
        );
      }
    );

    needToMarkAsReadAnnouncements.forEach((announcement: SiteAnnouncement) => {
      authenticationManager.markAnnouncementAsRead(announcement);
    });
    window.dispatchEvent(new CustomEvent('announcements-read', {
      detail: {
        announcementIdList: needToMarkAsReadAnnouncements.map(announcement => announcement.id)
      }
    }));
  }, [page, sortedAnnouncements, firstPagesAnnouncements, authenticationManager, isAdmin]);

  const getAnnouncementRowClasses = useCallback((announcement: SiteAnnouncement): string => {
    if (!authenticationManager || isAdmin) {
      return '';
    }
    if (!firstPagesAnnouncements.find((firstPageAnnouncement: SiteAnnouncement) => {
      return firstPageAnnouncement.id === announcement.id;
    })) {
      return '';
    }
    return authenticationManager.hasReadAnnouncement(announcement) ? '' : styles.unread;
  }, [isAdmin, authenticationManager, firstPagesAnnouncements]);

  const onPageChange = useCallback((page: number) => {
    setPage(page);
  }, []);

  const onShowDeleteConfirmModal = useCallback((ids: number[]) => {
    setDeleteAnnouncementIds(ids);
    setShowDeleteConfirmModal(true);
  }, []);

  const onHideDeleteConfirmModal = useCallback(() => {
    setDeleteAnnouncementIds([]);
    setShowDeleteConfirmModal(false);
  }, []);

  const onConfirmDelete = useCallback(() => {
    executeDelete(deleteAnnouncementIds);
    setDeleteAnnouncementIds([]);
    setShowDeleteConfirmModal(false);
  }, [executeDelete, deleteAnnouncementIds]);

  return {
    loading,
    isAdmin,
    searchString,
    filteredAnnouncements,
    selectedAnnouncements,
    columns,
    batchOperations,
    onPageChange,
    onClearSelect,
    onHandleSearch,
    onHandleSelect,
    onHandleSelectPage,
    markAnnouncementsAsRead,
    getAnnouncementRowClasses,
    showDeleteConfirmModal,
    deleteAnnouncementIds,
    onShowDeleteConfirmModal,
    onHideDeleteConfirmModal,
    onConfirmDelete
  };
};
